
import * as React from "react"
import { cn } from "@/lib/utils"

const ModernTable = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto rounded-xl bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-xl border border-purple-500/20 shadow-xl p-1">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
))
ModernTable.displayName = "ModernTable"

const ModernTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("border-b border-purple-500/30", className)} {...props} />
))
ModernTableHeader.displayName = "ModernTableHeader"

const ModernTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
ModernTableBody.displayName = "ModernTableBody"

const ModernTableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-purple-500/20 transition-colors hover:bg-purple-500/10",
      className
    )}
    {...props}
  />
))
ModernTableRow.displayName = "ModernTableRow"

const ModernTableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-4 text-left align-middle font-medium bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
))
ModernTableHead.displayName = "ModernTableHead"

const ModernTableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-4 align-middle text-purple-100 [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
))
ModernTableCell.displayName = "ModernTableCell"

// New condensed cell for showing food items with just name and quantity
const ModernFoodItemCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & { 
    name: string; 
    quantity: number;
    unit?: string;
  }
>(({ className, name, quantity, unit = "g", ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-3 align-middle text-purple-100 [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  >
    <span className="font-medium">{name}</span> <span className="text-purple-300 text-sm">{quantity}{unit}</span>
  </td>
))
ModernFoodItemCell.displayName = "ModernFoodItemCell"

export {
  ModernTable,
  ModernTableHeader,
  ModernTableBody,
  ModernTableRow,
  ModernTableHead,
  ModernTableCell,
  ModernFoodItemCell
}
